import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import "./HeroSection.scss";
import heroImg from "../assets/hero-right.png";
import { isIndependenceWindow, IDAY } from "../lib/independenceDay";
import AsOfNote from "./AsOfNote";
import { impact } from "../data/stats";
import {
  ArrowUpRight, Play, Users, BookOpen, BarChart3, MapPin,
  GraduationCap, Tv, Bot, FlaskConical, Lightbulb, ChevronDown,
} from "lucide-react";

// One formatter for every counted figure, so the hero cannot print a number
// in a shape no other page uses.
const inFmt = (n) => `${n.toLocaleString("en-IN")}+`;

const METRICS = [
  { icon: Users,         raw: impact.students.n,  fmt: inFmt,              label: impact.students.label  },
  { icon: BookOpen,      raw: impact.schools.n,   fmt: inFmt,              label: impact.schools.label   },
  { icon: BarChart3,     raw: impact.workshops.n, fmt: inFmt,              label: impact.workshops.label },
  { icon: GraduationCap, raw: impact.teachers.n,  fmt: inFmt,              label: impact.teachers.label  },
  { icon: MapPin,        raw: 0,                  fmt: () => `Pan India`,  label: "Reach"                },
];

const CARDS = [
  { icon: Bot,           bg: "#eff6ff", color: "#0041e8", title: "Medha AI",                      desc: "AI Assistant for Teachers & Students"    },
  { icon: FlaskConical,  bg: "#f0fdf4", color: "#16a34a", title: "STEM & Robotics Labs",          desc: "Hands-on learning for future innovators" },
  { icon: GraduationCap, bg: "#fdf4ff", color: "#9333ea", title: "Teacher Empowerment",           desc: "Training, AI tools & continuous support" },
  { icon: Lightbulb,     bg: "#fff7ed", color: "#ea580c", title: "Ideation to Innovation",         desc: "Hackathons, startup mindset & real skills"               },
];

// ─── Animated metric counter ──────────────────────────────────────────────────

function MetricCounter({ raw, fmt, duration = 1500 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const t0 = performance.now();
          const tick = (now) => {
            const p = Math.min((now - t0) / duration, 1);
            const eased = 1 - (1 - p) ** 3;
            setCount(Math.round(eased * raw));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [raw, duration]);

  return <span ref={ref}>{fmt(count)}</span>;
}

function MagneticBtn({ children, className, onClick }) {
  return (
    <button className={className} onClick={onClick}>
      {children}
    </button>
  );
}

// ─── Animation helper ─────────────────────────────────────────────────────────

const anim = (d = 0) => ({
  initial:    { opacity: 0, y: 20 },
  animate:    { opacity: 1, y: 0  },
  transition: { duration: 0.52, delay: d, ease: [0.22, 1, 0.36, 1] },
});

// ─── Component ────────────────────────────────────────────────────────────────

export default function HeroSection() {
  const sectionRef = useRef(null);
  const iday = isIndependenceWindow();

  return (
    <section ref={sectionRef} className="hs-wrap">


      <div className="hs__blob hs__blob--1" />
      <div className="hs__blob hs__blob--2" />
      <div className="hs__blob hs__blob--3" />

      <div className="hs">

        {/* ── LEFT ────────────────────────────────────────────────────── */}
        <div className="hs__left">

          {iday && (
            <motion.div className="iday-chip iday-chip--hero" {...anim(0)}>
              <span className="iday-chip__flag"><i /><i /><i /></span>
              <span>{IDAY.eyebrow}</span>
              <span className="iday-chip__sep">·</span>
              <span className="iday-chip__accent">
                <strong>{IDAY.yearsFree} years</strong> of freedom
              </span>
            </motion.div>
          )}

          <motion.div
            className="hs__badge"
            {...anim(0)}
            onClick={() => document.getElementById("shark-tank")?.scrollIntoView({ behavior: "smooth" })}
            style={{ cursor: "pointer" }}
          >
            <span className="hs__badge-dot" />
            <Tv size={12} strokeWidth={2} />
            As seen on&nbsp;<strong>Shark Tank India</strong>
          </motion.div>

          {/* Mask-reveal heading — text slides up through clip */}
          {/* Desktop: 2 lines */}
          <h1 className="hs__h1 hs__h1--desktop">
            <span className="hs__h1-line">
              <motion.span
                className="hs__h1-inner"
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.72, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                Nurturing India
              </motion.span>
            </span>
            <span className="hs__h1-line">
              <motion.span
                className="hs__h1-inner"
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.72, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="hs__h1-accent">Block by Block</span>
              </motion.span>
            </span>
          </h1>

          {/* Mobile: 2 lines */}
          <h1 className="hs__h1 hs__h1--mobile">
            <span className="hs__h1-line">
              <motion.span
                className="hs__h1-inner"
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.72, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                Nurturing India
              </motion.span>
            </span>
            <span className="hs__h1-line">
              <motion.span
                className="hs__h1-inner"
                initial={{ y: "105%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.72, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="hs__h1-accent">Block by Block</span>
              </motion.span>
            </span>
          </h1>

          <motion.p className="hs__desc" {...anim(0.14)}>
            CoGrad is transforming rural schools into{" "}
            <span className="hs__hl" style={{ "--ud": "0.9s" }}>AI-powered</span>, future-ready
            learning ecosystems through{" "}
            <span className="hs__hl" style={{ "--ud": "1.1s" }}>STEM, Robotics</span>,{" "}
            <a href="https://medha.cograd.in/" target="_blank" rel="noopener noreferrer" className="hs__hl" style={{ "--ud": "1.3s" }}>Medha AI</a> and{" "}
            <span className="hs__hl" style={{ "--ud": "1.5s" }}>Teacher Empowerment</span>.
          </motion.p>

          {/* Magnetic CTA buttons */}
          <motion.div className="hs__ctas" {...anim(0.2)}>
            <MagneticBtn
              className="hs__btn hs__btn--fill"
              onClick={() => window.open("https://medha.cograd.in/", "_blank", "noopener,noreferrer")}
            >
              Try Medha AI <ArrowUpRight size={16} />
            </MagneticBtn>
            <MagneticBtn
              className="hs__btn hs__btn--ghost"
              onClick={() => document.getElementById("shark-tank")?.scrollIntoView({ behavior: "smooth" })}
            >
              <span className="hs__play-ring"><Play size={11} fill="currentColor" /></span>
              Watch Impact Story
            </MagneticBtn>
          </motion.div>

          <motion.div className="hs__trust" {...anim(0.26)}>
            <div className="hs__trust-dot" />
            <p>
              Trusted by <strong>schools, teachers &amp; learners</strong>
              <span className="hs__trust-geo"> across India</span>
            </p>
          </motion.div>

        </div>

        {/* ── RIGHT — floating cards + parallax image ──────────────────── */}
        <div className="hs__right">

          {/* Staggered floating cards */}
          <div className="hs__cards">
            {CARDS.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.a
                  key={i}
                  href="#"
                  className="hs__card"
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: 0.18 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <span className="hs__card-ico" style={{ background: card.bg, color: card.color }}>
                    <Icon size={14} />
                  </span>
                  <div className="hs__card-body">
                    <div className="hs__card-title">{card.title}</div>
                    <div className="hs__card-desc">{card.desc}</div>
                  </div>
                </motion.a>
              );
            })}
          </div>

          {/* Image */}
          <motion.div
            className="hs__img-wrap"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={heroImg}
              alt="CoGrad student"
              className="hs__img"
            />

          </motion.div>

        </div>
      </div>

      {/* ── METRICS ─────────────────────────────────────────────────────── */}
      <motion.div className="hs__metrics"
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}>
        {METRICS.map((m, i) => {
          const Icon = m.icon;
          return (
            <React.Fragment key={i}>
              <div className="hs__metric">
                <span className="hs__metric-ico"><Icon size={17} /></span>
                <div>
                  <div className="hs__metric-val">
                    <MetricCounter raw={m.raw} fmt={m.fmt} />
                  </div>
                  <div className="hs__metric-lbl">{m.label}</div>
                </div>
              </div>
              {i < METRICS.length - 1 && <div className="hs__metric-sep" />}
            </React.Fragment>
          );
        })}
      </motion.div>

      {/* Outside the metrics bar on purpose: that bar is a flex row whose
          children are metrics and separators, and on mobile it hides every
          child past the third. */}
      <AsOfNote className="asof--center hs__asof" />

      {/* ── SCROLL INDICATOR ────────────────────────────────────────────── */}
      <motion.div
        className="hs__scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        onClick={() => document.querySelector(".hs__metrics")
          ?.nextElementSibling?.scrollIntoView({ behavior: "smooth" })}
      >
        <ChevronDown size={18} strokeWidth={2} />
      </motion.div>

    </section>
  );
}
